import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LangProvider, useLang } from "./contexts/LangContext";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import PartnersCarousel from "./components/PartnersCarousel";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import StaffRoute from "./components/StaffRoute";
import SeoHead, { useLangUrlSync } from "./components/SeoHead";
import { Toaster } from "sonner";

import Home from "./pages/Home";
import LeClub from "./pages/LeClub";
import Agenda from "./pages/Agenda";
import Voile from "./pages/Voile";
import Regattas from "./pages/Regattas";
import RegattaDetail from "./pages/RegattaDetail";
import Stages from "./pages/Stages";
import Motor from "./pages/Motor";
import Boutique from "./pages/Boutique";
import Product from "./pages/Product";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import CheckoutComplete from "./pages/CheckoutComplete";
import Galerie from "./pages/Galerie";
import GalleryAlbum from "./pages/GalleryAlbum";
import Contact from "./pages/Contact";

import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import Treasury from "./pages/Treasury";
import Secretariat from "./pages/Secretariat";
import President from "./pages/President";
import Gestion from "./pages/Gestion";

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LangSync() {
  useLangUrlSync();
  return null;
}

function Routed() {
  return (
    <Routes>
      <Route index element={<Layout><SeoHead /><Home /></Layout>} />
      <Route path="club" element={<Layout><SeoHead title="Le Club" /><LeClub /></Layout>} />
      <Route path="agenda" element={<Layout><SeoHead title="Agenda" /><Agenda /></Layout>} />
      <Route path="voile" element={<Layout><SeoHead title="Voile" /><Voile /></Layout>} />
      <Route path="regattas" element={<Layout><SeoHead title="Régates" /><Regattas /></Layout>} />
      <Route path="regattas/:slug" element={<Layout><SeoHead title="Régate" /><RegattaDetail /></Layout>} />
      <Route path="stages" element={<Layout><SeoHead title="Stages" /><Stages /></Layout>} />
      <Route path="motor" element={<Layout><SeoHead title="Moteur" /><Motor /></Layout>} />
      <Route path="shop" element={<Layout><SeoHead title="Boutique" /><Boutique /></Layout>} />
      <Route path="shop/:id" element={<Layout><SeoHead title="Produit" /><Product /></Layout>} />
      <Route path="cart" element={<Layout><SeoHead title="Panier" /><Cart /></Layout>} />
      <Route path="checkout" element={<Layout><SeoHead title="Checkout" /><Checkout /></Layout>} />
      <Route path="checkout/complete" element={<Layout><SeoHead title="Confirmation" /><CheckoutComplete /></Layout>} />
      <Route path="gallery" element={<Layout><SeoHead title="Galerie" /><Galerie /></Layout>} />
      <Route path="galerie" element={<Layout><SeoHead title="Galerie" /><Galerie /></Layout>} />
      <Route path="galerie/:slug" element={<Layout><SeoHead title="Album" /><GalleryAlbum /></Layout>} />
      <Route path="gallery/:slug" element={<Layout><SeoHead title="Album" /><GalleryAlbum /></Layout>} />
      <Route path="contact" element={<Layout><SeoHead title="Contact" /><Contact /></Layout>} />

      <Route path="login" element={<Layout hideFooter><SeoHead title="Connexion" /><Login /></Layout>} />
      <Route path="register" element={<Layout hideFooter><SeoHead title="Inscription" /><Register /></Layout>} />
      <Route path="verify-email" element={<Layout hideFooter><SeoHead title="Vérification" /><VerifyEmail /></Layout>} />
      <Route path="forgot-password" element={<Layout hideFooter><ForgotPassword /></Layout>} />
      <Route path="reset-password" element={<Layout hideFooter><ResetPassword /></Layout>} />

      <Route path="dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="admin" element={<AdminRoute><Layout><AdminDashboard /></Layout></AdminRoute>} />
      <Route path="treasury" element={<StaffRoute allowed={["admin", "treasurer"]}><Layout><Treasury /></Layout></StaffRoute>} />
      <Route path="secretariat" element={<StaffRoute allowed={["admin", "secretary"]}><Layout><Secretariat /></Layout></StaffRoute>} />
      <Route path="president" element={<StaffRoute allowed={["admin", "president"]}><Layout><President /></Layout></StaffRoute>} />
      <Route path="gestion" element={<StaffRoute allowed={["admin", "manager"]}><Layout><Gestion /></Layout></StaffRoute>} />

      <Route path="*" element={<Layout><NotFound /></Layout>} />
    </Routes>
  );
}

function NotFound() {
  const { lang, t } = useLang();
  return (
    <div className="pt-40 pb-32 text-center px-6" data-testid="not-found-page">
      <div className="label-eyebrow text-club-red mb-4">404</div>
      <h1 className="font-serif text-4xl lg:text-5xl font-light text-navy">
        {{ fr: "Page introuvable", nl: "Pagina niet gevonden", en: "Page not found" }[lang]}
      </h1>
      <p className="text-slate-600 mt-4">
        {{ fr: "La page que vous cherchez n'existe pas ou a été déplacée.", nl: "De pagina die u zoekt bestaat niet of is verplaatst.", en: "The page you are looking for does not exist or has been moved." }[lang]}
      </p>
      <Link to={`/${lang}`} className="inline-block mt-8 bg-navy text-white px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-club-red transition-colors" data-testid="not-found-home-link">
        {t.nav.home}
      </Link>
    </div>
  );
}

function Layout({ children, hideFooter }) {
  return (
    <div className="App min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      {!hideFooter && <PartnersCarousel />}
      {!hideFooter && <Footer />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LangProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollToTop />
              <LangSync />
              <Toaster position="top-right" richColors closeButton />
              <Routes>
                {/* Multi-locale prefixed routes — primary URLs */}
                <Route path="/:lang/*" element={<Routed />} />
                {/* Legacy un-prefixed routes — auto-redirect via LangSync */}
                <Route path="/*" element={<Routed />} />
              </Routes>
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </LangProvider>
    </HelmetProvider>
  );
}

export default App;
